import React, { useState } from 'react';
import { X, Search, Users, Crown } from 'lucide-react';
import { money, pct } from '../../lib/format';
import RatingBadge from '../Staff/RatingBadge';
import useStaffScores from '../../lib/useStaffScores';

const StaffSelector = ({ staff, selectedEmails, onChange, costing }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const scores = useStaffScores();

  const figuresFor = (email) =>
    (costing?.staff || []).find((row) => row.staff_email === email) || null;

  const byProfit = (a, b) => {
    const left = figuresFor(a.email)?.projected_profit_gbp;
    const right = figuresFor(b.email)?.projected_profit_gbp;
    if (left === right) return 0;
    if (left === null || left === undefined) return 1;
    if (right === null || right === undefined) return -1;
    return right - left;
  };

  // Travel money and profit exactly as the Profit per job screen works it out
  const CostingLine = ({ email }) => {
    const figures = figuresFor(email);
    if (!figures) return null;
    if (figures.travel === null) {
      return (
        <p className="text-[11px] font-semibold text-amber-700">{figures.travel_message}</p>
      );
    }
    return (
      <p className="text-[11px] text-slate-500">
        Travel {money(figures.travel)}
        {figures.projected_profit_gbp !== null && (
          <> · profit {money(figures.projected_profit_gbp)} ({pct(figures.projected_profit_percentage)})</>
        )}
        {figures.best_margin && (
          <span className="ml-1 font-bold text-gold-deep">
            <Crown className="mr-0.5 inline h-3 w-3" />best margin
          </span>
        )}
      </p>
    );
  };

  const activeStaff = staff.filter(s => s.active);
  
  const filteredStaff = activeStaff.filter(member =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedStaff = activeStaff.filter(s => selectedEmails.includes(s.email));
  const unselectedStaff = filteredStaff
    .filter(s => !selectedEmails.includes(s.email))
    .sort(byProfit);

  const addStaff = (email) => {
    if (!selectedEmails.includes(email)) {
      onChange([...selectedEmails, email]);
    }
    setSearchQuery('');
  };

  const removeStaff = (email) => {
    onChange(selectedEmails.filter(e => e !== email));
  };

  return (
    <div className="space-y-2">
      {/* Selected Staff Chips */}
      {selectedStaff.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
          {selectedStaff.map(member => (
            <div
              key={member.email}
              className="flex items-center gap-1 bg-blue-500 text-white px-3 py-1.5 rounded-full text-sm font-medium"
            >
              <span>{member.name}</span>
              <button
                type="button"
                onClick={() => removeStaff(member.email)}
                className="hover:bg-blue-600 rounded-full p-0.5 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search/Add Staff */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search and add staff members..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
          />
        </div>

        {/* Dropdown Results */}
        {showDropdown && searchQuery && unselectedStaff.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {unselectedStaff.map(member => (
              <button
                key={member.email}
                type="button"
                onClick={() => addStaff(member.email)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-100 transition-colors flex items-center justify-between"
              >
                <div>
                  <p className="font-medium text-gray-900">{member.name}</p>
                  <p className="text-xs text-gray-500">{member.email}</p>
                  <RatingBadge score={scores[member.email]?.score}
                               reliability={scores[member.email]?.reliability} />
                  <CostingLine email={member.email} />
                </div>
                <span className="text-xs text-gray-400">Add</span>
              </button>
            ))}
          </div>
        )}

        {/* No results */}
        {showDropdown && searchQuery && unselectedStaff.length === 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-center text-gray-500 text-sm">
            {selectedEmails.includes(filteredStaff[0]?.email) ? 'Already selected' : 'No staff found'}
          </div>
        )}
      </div>

      {/* Browse All Staff */}
      {!searchQuery && (
        <div className="border border-hair rounded-[10px] p-3 bg-page max-h-56 overflow-y-auto">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
            <Users size={16} />
            <span>Available staff ({unselectedStaff.length}){costing ? ', best margin first' : ''}</span>
          </div>
          <div className="space-y-1">
            {unselectedStaff.slice(0, costing ? 20 : 10).map(member => (
              <button
                key={member.email}
                type="button"
                onClick={() => addStaff(member.email)}
                className="w-full text-left px-3 py-2 hover:bg-white hover:shadow-sm rounded transition-all text-sm flex items-start justify-between gap-2"
                data-testid={`pick-staff-${member.email}`}
              >
                <span className="min-w-0">
                  <span className="block text-gray-900">{member.name}</span>
                  <RatingBadge score={scores[member.email]?.score}
                               reliability={scores[member.email]?.reliability}
                               testId={`pick-rating-${member.email}`} />
                  <CostingLine email={member.email} />
                </span>
                <span className="text-xs text-gold-deep">+ Add</span>
              </button>
            ))}
            {unselectedStaff.length > (costing ? 20 : 10) && (
              <p className="text-xs text-gray-500 pt-2 text-center">
                {unselectedStaff.length - (costing ? 20 : 10)} more… use search to find
              </p>
            )}
          </div>
        </div>
      )}

      {/* Helper Text */}
      <p className="text-xs text-gray-500">
        {selectedStaff.length === 0 
          ? 'Leave unassigned to make job available to all staff'
          : `Job assigned to ${selectedStaff.length} staff member${selectedStaff.length !== 1 ? 's' : ''}`
        }
      </p>

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default StaffSelector;
